import { ExpandMoreRounded } from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers';
import { useState } from 'react';

import { AnalyticsService, EVENTS } from '@/services';
import { Referrer } from '@/types';

import { TextField } from '../TextField';

export const DatePickerVariants = {
  OUTLINED: 'outlined',
  FILLED: 'filled',
} as const;

export interface DatePickerProps extends MuiDatePickerProps<Date> {
  variant?: (typeof DatePickerVariants)[keyof typeof DatePickerVariants];
  allowTextInput?: boolean;
  allowClear?: boolean;
  label?: string;
  format?: string;
  referrer?: Referrer;
  error?: boolean;
  fullWidth?: boolean;
}

export const DatePicker = ({
  variant = DatePickerVariants.OUTLINED,
  allowTextInput = true,
  allowClear = true,
  label,
  referrer = {},
  format = 'MMM d, yyyy',
  value,
  onChange,
  error,
  fullWidth = false,
  ...muiProps
}: DatePickerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);

  return (
    <MuiDatePicker
      {...muiProps}
      open={!allowTextInput ? open : undefined}
      onOpen={!allowTextInput ? () => setOpen(true) : undefined}
      onClose={!allowTextInput ? () => setOpen(false) : undefined}
      value={value || null}
      onChange={(v, context) => {
        onChange?.(v, context);
        AnalyticsService.logEvent(EVENTS.DATE_SELECT, {
          location: {
            ...referrer,
          },
          previous_date: value?.toISOString() || '',
          selected_date: v?.toISOString() || '',
        });
      }}
      format={format}
      slots={{
        textField: TextField,
        openPickerIcon: ExpandMoreRounded,
      }}
      slotProps={{
        desktopPaper: {
          sx: {
            mt: 1,
            borderRadius: 3,
          },
        },
        day: {
          sx: {
            '&.MuiPickersDay-root.Mui-selected': {
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'secondary.main',
              },
            },
          },
        },
        field: {
          clearable: allowClear,
          readOnly: !allowTextInput,
        },
        textField: {
          size: 'small',
          label: isMobile ? label : undefined,
          onClick: !allowTextInput ? () => setOpen(true) : undefined,
          error: error === true,
          sx: [
            variant === DatePickerVariants.FILLED && styles.filledVariant,
            !allowTextInput && {
              '& .MuiInputBase-input': {
                cursor: 'pointer',
              },
            },
            { width: fullWidth ? '100%' : 200 },
          ],
        },
      }}
    />
  );
};

const styles = {
  filledVariant: {
    '& .MuiInputBase-root': {
      backgroundColor: 'grey50',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:focus .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
};
